<template>
  <transition name="preview-fade">
    <div v-if="internalShowModal" class="qpf-fullscreen">
      <!-- Header -->
      <div class="qpf-header">
        <div class="qpf-header-left">
          <button class="qpf-back" @click="handleClose" aria-label="Volver">
            <i class="el-icon-arrow-left"></i>
          </button>
          <h5 class="qpf-title" v-if="quote">
            <template v-if="quote._loading || !quote.code">
              Cargando cotización...
            </template>
            <template v-else>Cotización N° {{ quote.code }}</template>
          </h5>
        </div>
        <div class="qpf-header-right">
          <button
            class="qpf-action-btn"
            @click="$emit('action', 'quote-send')"
            title="Enviar"
          >
            <i class="el-icon-s-promotion"></i>
          </button>
          <button
            v-if="quote && quote.enable_reminders !== undefined"
            class="qpf-action-btn"
            :class="{
              'qpf-action-btn--reminder-on': quote.enable_reminders
            }"
            :title="
              quote.enable_reminders
                ? 'Pausar recordatorios'
                : 'Activar recordatorios'
            "
            @click="toggleReminders(!quote.enable_reminders)"
          >
            <i class="el-icon-bell"></i>
          </button>
          <button
            class="qpf-action-btn"
            @click="$emit('action', 'quote-download')"
            title="Descargar PDF"
          >
            <i class="el-icon-download"></i>
          </button>
          <el-dropdown trigger="click" @command="cmd => $emit('action', cmd)">
            <button class="qpf-action-btn">
              <i class="el-icon-more"></i>
            </button>
            <el-dropdown-menu slot="dropdown" class="qpf-dropdown-menu">
              <el-dropdown-item command="quote-view-doc">
                <i class="el-icon-document"></i> Ver documento completo
              </el-dropdown-item>
              <el-dropdown-item command="quote-send">
                <i class="el-icon-s-promotion"></i> Dar seguimiento
              </el-dropdown-item>
              <el-dropdown-item
                v-if="status === 'draft' || status === 'pending'"
                command="quote-edit"
              >
                <i class="el-icon-edit"></i> Editar
              </el-dropdown-item>
              <el-dropdown-item
                v-if="
                  status === 'draft' ||
                    status === 'pending' ||
                    status === 'partial' ||
                    status === 'preapproved'
                "
                command="quote-convert-invoice"
              >
                <i class="el-icon-document-checked"></i> Convertir en factura
              </el-dropdown-item>
              <el-dropdown-item
                v-if="status === 'preapproved'"
                command="quote-change-status-pending"
                divided
              >
                <i class="el-icon-refresh-left"></i> Cambiar a pendiente
              </el-dropdown-item>
              <el-dropdown-item
                v-if="status === 'draft' || status === 'pending'"
                command="quote-delete"
                class="text-danger"
              >
                <i class="el-icon-delete"></i> Eliminar
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <button class="qpf-close" @click="handleClose" aria-label="Cerrar">
            <i class="el-icon-close"></i>
          </button>
        </div>
      </div>

      <!-- Skeleton loader mientras se carga la cotización -->
      <div class="qpf-body" v-if="!quote || quote._loading || !getVersionQuote">
        <b-container fluid class="pb-4 px-lg-4">
          <div class="qpf-skeleton">
            <div class="qpf-sk-card">
              <div class="qpf-sk-row">
                <div class="qpf-sk-block qpf-sk-block--logo"></div>
                <div class="qpf-sk-stack">
                  <div class="qpf-sk-line qpf-sk-line--w60"></div>
                  <div class="qpf-sk-line qpf-sk-line--w40"></div>
                  <div class="qpf-sk-line qpf-sk-line--w50"></div>
                </div>
              </div>
              <div class="qpf-sk-divider"></div>
              <div class="qpf-sk-grid">
                <div>
                  <div
                    class="qpf-sk-line qpf-sk-line--small qpf-sk-line--w40"
                  ></div>
                  <div class="qpf-sk-line qpf-sk-line--w70"></div>
                </div>
                <div>
                  <div
                    class="qpf-sk-line qpf-sk-line--small qpf-sk-line--w40"
                  ></div>
                  <div class="qpf-sk-line qpf-sk-line--w70"></div>
                </div>
                <div>
                  <div
                    class="qpf-sk-line qpf-sk-line--small qpf-sk-line--w40"
                  ></div>
                  <div class="qpf-sk-line qpf-sk-line--w50"></div>
                </div>
              </div>
              <div class="qpf-sk-divider"></div>
              <div class="qpf-sk-table">
                <div class="qpf-sk-line qpf-sk-line--w90"></div>
                <div class="qpf-sk-line qpf-sk-line--w80"></div>
                <div class="qpf-sk-line qpf-sk-line--w85"></div>
              </div>
            </div>
          </div>
        </b-container>
      </div>

      <!-- Body scrolleable -->
      <div class="qpf-body" v-if="quote && !quote._loading && getVersionQuote">
        <b-container fluid class="pb-4 px-lg-4">
          <b-row>
            <b-col>
              <QuoteListVersionsComponent
                :quote="quote"
                @onClickVersion="handleOnClickVersion"
              />
            </b-col>
          </b-row>
          <b-row v-if="quote">
            <b-col>
              <QuoteTimeline :quoteId="quote.id" class="mb-3 mt-2" />
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <div class="card box-shadow card-quotes-view py-4">
                <div class="card-body">
                  <div class="card-quotes-view-header">
                    <!-- Mobile header -->
                    <DocumentViewHeader
                      class="d-block d-md-none"
                      doc-type="Cotización"
                      :doc-number="quote.code"
                      :business="{
                        logo: getUser.user_business.business_logo,
                        name: getVersionQuote.name,
                        ruc: getUser.user_business.business_ruc
                      }"
                      :date="getVersionQuote.date_at"
                      :status="quote.status"
                      :client="{
                        name: contactName,
                        document: quote.client.document || '',
                        email: quote.client.email || ''
                      }"
                      :meta-items="[
                        {
                          label: 'Validez',
                          value: getVersionQuote.validate_at
                        },
                        {
                          label: 'Versión',
                          value: 'v' + getVersionQuote.version_number
                        }
                      ]"
                    />

                    <!-- Desktop header -->
                    <b-row class="d-none d-md-flex">
                      <b-col sm="12" md="12" lg="6">
                        <div class="card-quotes-view-logo mb-4">
                          <b-row>
                            <b-col sm="12" md="5" lg="4">
                              <div class="logo">
                                <img
                                  v-if="getUser.user_business.business_logo"
                                  :src="getUser.user_business.business_logo"
                                  class="image"
                                  alt="Empresa"
                                  style="width: 100%; max-width: 200px; height: auto;"
                                />
                              </div>
                            </b-col>
                            <b-col sm="12" md="7" lg="8">
                              <div class="info">
                                <p class="mb-2 text-color-secondary font-bold">
                                  Versión: {{ getVersionQuote.version_number }}
                                </p>
                                <h4 class="h4 font-bold m-0">
                                  N° {{ quote.code }}
                                </h4>
                                <h2 class="h2 font-bold m-0">
                                  {{ getVersionQuote.name }}
                                </h2>
                                <p class="font-semi-bold mt-2">
                                  RUC: {{ getUser.user_business.business_ruc }}
                                </p>
                                <p class="font-semi-bold">
                                  {{ getUser.user_business.business_address }}
                                </p>
                              </div>
                            </b-col>
                          </b-row>
                        </div>
                      </b-col>
                      <b-col sm="12" md="12" lg="6">
                        <div class="card-quotes-view-info">
                          <b-row>
                            <b-col sm="4" md="4" lg="4">
                              <label class="font-semi-bold d-block"
                                >Emisión</label
                              >
                              <p class="m-0">{{ getVersionQuote.date_at }}</p>
                            </b-col>
                            <b-col sm="4" md="4" lg="4">
                              <label class="font-semi-bold d-block"
                                >Validez</label
                              >
                              <p class="m-0">
                                {{ getVersionQuote.validate_at }}
                              </p>
                            </b-col>
                            <b-col sm="4" md="4" lg="4">
                              <label class="font-semi-bold d-block mb-2"
                                >Status</label
                              >
                              <StatusReadDocumentsComponent
                                :status="quote.status"
                              />
                            </b-col>
                          </b-row>
                          <b-row class="mt-4">
                            <b-col sm="4" md="4" lg="4">
                              <label class="font-semi-bold d-block"
                                >Contacto</label
                              >
                              <p class="m-0">{{ contactName }}</p>
                            </b-col>
                            <b-col sm="4" md="4" lg="4">
                              <label class="font-semi-bold d-block"
                                >Dirección</label
                              >
                              <p class="m-0">{{ quote.client.address }}</p>
                            </b-col>
                            <b-col sm="4" md="4" lg="4">
                              <label class="font-semi-bold d-block"
                                >Teléfono</label
                              >
                              <p class="m-0">
                                {{ getUser.user_business.business_phone }}
                              </p>
                            </b-col>
                          </b-row>
                        </div>
                      </b-col>
                    </b-row>
                  </div>

                  <div class="card-quotes-view-body mt-5">
                    <div class="card-quotes-view-body-products">
                      <!-- Desktop tabla -->
                      <div class="d-none d-md-block">
                        <div class="card-quotes-view-table table-items">
                          <b-row class="table-items-header">
                            <b-col md="1">
                              <h6 class="h6 font-bold mb-0 ml-2">Código</h6>
                            </b-col>
                            <b-col md="5">
                              <h6 class="h6 font-bold mb-0">Detalle</h6>
                            </b-col>
                            <b-col md="2">
                              <h6 class="h6 font-bold mb-0">Cantidad</h6>
                            </b-col>
                            <b-col md="2">
                              <h6 class="h6 font-bold mb-0">Precio U.</h6>
                            </b-col>
                            <b-col md="2">
                              <h6 class="h6 font-bold mb-0">Subtotal</h6>
                            </b-col>
                          </b-row>
                        </div>
                        <b-row
                          class="mt-4 d-flex"
                          v-for="(product, index) in getVersionQuote.products"
                          :key="'d' + index"
                        >
                          <b-col md="1">
                            <p class="m-0 ml-2">{{ product.product.code }}</p>
                          </b-col>
                          <b-col md="5">
                            <p class="m-0">
                              <strong>{{ product.product.name }}</strong>
                              <span
                                v-if="product.product_detail"
                                v-html="product.product_detail"
                                class="text-pre-wrap d-block mt-1 text-muted"
                              ></span>
                            </p>
                          </b-col>
                          <b-col md="2">
                            <p class="m-0">{{ product.product_quantity }}</p>
                          </b-col>
                          <b-col md="2">
                            <p class="m-0">${{ product.product_amount }}</p>
                          </b-col>
                          <b-col md="2">
                            <p class="m-0">
                              ${{
                                handleCalculateSubTotal(
                                  product.product_quantity,
                                  product.product_amount
                                )
                              }}<span
                                v-if="getVersionQuote.hide_total"
                                class="plus-iva"
                                >+IVA</span
                              >
                            </p>
                          </b-col>
                        </b-row>
                      </div>

                      <!-- Mobile: tarjetas por ítem -->
                      <div class="d-block d-md-none doc-mobile-items">
                        <div
                          v-for="(product, index) in getVersionQuote.products"
                          :key="'m' + index"
                          class="doc-item"
                        >
                          <div class="doc-item-row">
                            <span class="doc-item-name">{{
                              product.product.name
                            }}</span>
                            <span class="doc-item-total">
                              ${{
                                handleCalculateSubTotal(
                                  product.product_quantity,
                                  product.product_amount
                                )
                              }}<span
                                v-if="getVersionQuote.hide_total"
                                class="plus-iva"
                                >+IVA</span
                              >
                            </span>
                          </div>
                          <div v-if="product.product.code" class="doc-item-sku">
                            SKU: {{ product.product.code }}
                          </div>
                          <div class="doc-item-qty">
                            Cant: {{ product.product_quantity }} × ${{
                              product.product_amount
                            }}
                          </div>
                          <div
                            v-if="product.product_detail"
                            v-html="product.product_detail"
                            class="doc-item-detail text-pre-wrap"
                          ></div>
                        </div>
                      </div>
                    </div>

                    <!-- Mobile totals -->
                    <div class="d-block d-md-none">
                      <DocumentCalculator
                        readOnly
                        :setProducts="getVersionQuote.products"
                        :setDisc="getVersionQuote.discount"
                        :setHideTotal="!!getVersionQuote.hide_total"
                      />
                      <div class="doc-payment">
                        <span class="doc-payment-label">Forma de pago</span>
                        <span class="doc-payment-value">
                          <template v-if="getVersionQuote.method_payment === 1"
                            >De contado</template
                          >
                          <template
                            v-else-if="getVersionQuote.method_payment === 2"
                            >Crédito</template
                          >
                          <template
                            v-else-if="getVersionQuote.method_payment === 3"
                            >Anticipo / Contra entrega</template
                          >
                        </span>
                      </div>
                    </div>

                    <hr class="d-none d-md-block" />

                    <!-- Desktop totals + especificaciones -->
                    <b-row class="d-none d-md-flex">
                      <b-col xs="12" sm="12" md="6">
                        <div
                          class="card-quotes-view-aditionals bg-color-gray p-4"
                        >
                          <p class="font-semi-bold">
                            Especificaciones Adicionales
                          </p>
                          <b-row
                            class="my-2"
                            v-for="(specification,
                            index) in getVersionQuote.additional_specifications"
                            :key="index"
                          >
                            <b-col xs="10" sm="10" md="10"
                              >- {{ specification }}</b-col
                            >
                          </b-row>
                        </div>
                        <div class="card-quotes-view-method-pay mt-4 p-4">
                          <p class="font-semi-bold mb-2">Forma de pago</p>
                          <p
                            class="m-0"
                            v-if="getVersionQuote.method_payment === 1"
                          >
                            De contado
                          </p>
                          <div v-if="getVersionQuote.method_payment === 2">
                            <p class="m-0">Crédito</p>
                            <p class="m-0 text-muted" v-if="getLimitCredit">
                              {{ getLimitCredit.label }}
                              {{ getVersionQuote.limit_credit_at }}
                            </p>
                          </div>
                          <div v-if="getVersionQuote.method_payment === 3">
                            <p
                              class="m-0"
                              v-if="!parsedPaymentData.installments.length"
                            >
                              Anticipo / Contra entrega
                            </p>
                            <p
                              class="m-0 text-muted"
                              v-if="parsedPaymentData.text"
                              style="white-space: pre-line;"
                            >
                              {{ parsedPaymentData.text }}
                            </p>
                            <div
                              v-if="parsedPaymentData.installments.length"
                              class="installments-detail"
                            >
                              <div
                                v-for="(inst,
                                idx) in parsedPaymentData.installments"
                                :key="'inst-' + idx"
                                class="installment-row"
                              >
                                <div class="installment-info">
                                  <span class="installment-label">{{
                                    inst.label
                                  }}</span>
                                  <span class="installment-pct"
                                    >{{ inst.percentage }}%</span
                                  >
                                </div>
                                <span class="installment-amount"
                                  >${{ onParseFloatFormat(inst.amount) }}</span
                                >
                              </div>
                            </div>
                          </div>
                        </div>
                      </b-col>
                      <b-col xs="12" sm="12" md="4" offset-md="2">
                        <DocumentCalculator
                          readOnly
                          :setProducts="getVersionQuote.products"
                          :setDisc="getVersionQuote.discount"
                          :setHideTotal="!!getVersionQuote.hide_total"
                        />
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </div>
  </transition>
</template>

<script>
import { parseFloatFormat } from "@/assets/utils/format";
import { limitCredits } from "../assets/resources";
import quoteService from "../services/quoteService";
import DocumentCalculator from "../../../components/DocumentCalculator.vue";
import QuoteListVersionsComponent from "../components/QuoteListVersions";
import QuoteTimeline from "../components/QuoteTimeline";
import StatusReadDocumentsComponent from "../../../../components/StatusReadDocuments";
import DocumentViewHeader from "../../../components/DocumentViewHeader";

export default {
  name: "QuotePreviewComponent",
  data() {
    return {
      internalShowModal: false,
      limitCredits,
      quoteVersion: null
    };
  },
  props: {
    quote: {
      require: true
    },
    idVersion: {
      required: true
    },
    showModal: {
      type: Boolean,
      required: true
    },
    status: {
      type: String,
      default: ""
    }
  },
  // Nota: ya no movemos el $el al body. El patrón anterior rompía los
  // event handlers cuando se montaba el componente desde varios padres.
  // Confiamos en position:fixed + z-index alto del CSS.
  watch: {
    showModal: {
      immediate: true,
      handler(newVal) {
        this.internalShowModal = newVal;
        if (newVal) {
          document.addEventListener("keydown", this.onEscape);
          document.body.style.overflow = "hidden";
          this.toggleThirdPartyWidgets(true);
        } else {
          document.removeEventListener("keydown", this.onEscape);
          document.body.style.overflow = "";
          this.toggleThirdPartyWidgets(false);
        }
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEscape);
    document.body.style.overflow = "";
    if (typeof this.toggleThirdPartyWidgets === "function") {
      this.toggleThirdPartyWidgets(false);
    }
  },
  computed: {
    getUser() {
      return this.$store.getters.getUser;
    },
    getLimitCredit() {
      return limitCredits.find(
        limit => this.getVersionQuote.limit_credit === limit.value
      );
    },
    getVersionQuote() {
      return this.quote.versions.find(v => this.idVersion === v.id);
    },
    contactName() {
      return this.quote.contact
        ? this.quote.contact.name
        : this.quote.client.name;
    },
    parsedPaymentData() {
      var obs =
        this.getVersionQuote && this.getVersionQuote.observation_advance;
      if (!obs) return { text: "", installments: [] };

      var lines = obs.split("\n");
      var installments = [];
      var textLines = [];
      var inDetail = false;

      for (var i = 0; i < lines.length; i++) {
        var line = lines[i].trim();
        if (line === "Detalle de cuotas:") {
          inDetail = true;
          continue;
        }
        if (inDetail && line.indexOf("- ") === 0) {
          var match = line.match(/^- (.+?):\s*(\d+)%\s*—\s*\$(.+)$/);
          if (match) {
            installments.push({
              label: match[1],
              percentage: parseInt(match[2]),
              amount: parseFloat(match[3].replace(/,/g, ""))
            });
          }
        } else if (!inDetail && line) {
          textLines.push(line);
        }
      }

      return {
        text: textLines.join("\n"),
        installments: installments
      };
    }
  },
  methods: {
    toggleThirdPartyWidgets(hide) {
      const selectors = [".amo-button-holder", ".signing-toast-queue"];
      selectors.forEach(sel => {
        const el = document.querySelector(sel);
        if (el) el.style.display = hide ? "none" : "";
      });
    },
    handleCalculateSubTotal(quantity, price) {
      return parseFloatFormat(quantity * price);
    },
    handleClose() {
      this.internalShowModal = false;
      this.$emit("close");
    },
    onEscape(e) {
      if (e.key === "Escape") this.handleClose();
    },
    handleOnClickVersion(version) {
      this.idVersion = version.id;
    },
    onParseFloatFormat(value) {
      return parseFloatFormat(value);
    },
    async toggleReminders(val) {
      const previous = !!this.quote.enable_reminders;
      this.$set(this.quote, "enable_reminders", val);
      try {
        await quoteService.updateReminders(this.quote.id, {
          enable_reminders: val
        });
        this.$notifications.success({
          message: val
            ? "Recordatorios de seguimiento activados"
            : "Recordatorios de seguimiento pausados"
        });
      } catch (e) {
        this.$set(this.quote, "enable_reminders", previous);
        this.$notifications.error({
          message: "No se pudo actualizar los recordatorios"
        });
      }
    }
  },
  components: {
    DocumentCalculator,
    QuoteListVersionsComponent,
    QuoteTimeline,
    StatusReadDocumentsComponent,
    DocumentViewHeader
  }
};
</script>

<style lang="scss" scoped>
.plus-iva {
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  margin-left: 4px;
}
$primary: #31355c;
$border: #e4eaf6;

.qpf-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 2100;
  background: #f5f6fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qpf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
  background: #fff;
}
.qpf-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.qpf-title {
  font-size: 15px;
  font-weight: 600;
  color: $primary;
  margin: 0;
}
.qpf-back {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: $primary;
  transition: background 0.15s;
  &:hover {
    background: #f0f2f7;
  }
}
.qpf-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.qpf-action-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: $primary;
  transition: all 0.15s;
  &:hover {
    background: #f0f2f7;
    color: darken($primary, 10%);
  }
}
.qpf-action-btn--reminder-on {
  color: #3ecfb4 !important;
  &:hover {
    background: #f0fdfb !important;
    color: darken(#3ecfb4, 8%) !important;
  }
}
.qpf-close {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #909399;
  transition: all 0.15s;
  &:hover {
    background: #f0f2f7;
    color: $primary;
  }
}

// ── Skeleton loader ──
.qpf-skeleton {
  margin-top: 16px;
}
.qpf-sk-card {
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 10px;
  padding: 24px;
}
.qpf-sk-row {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 16px;
}
.qpf-sk-stack {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.qpf-sk-block {
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f2f5 25%, #e8eaed 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: qpfSkShimmer 1.4s infinite;

  &--logo {
    width: 140px;
    height: 60px;
    flex-shrink: 0;
  }
}
.qpf-sk-line {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f2f5 25%, #e8eaed 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: qpfSkShimmer 1.4s infinite;

  &--small {
    height: 9px;
  }
  &--w40 {
    width: 40%;
  }
  &--w50 {
    width: 50%;
  }
  &--w60 {
    width: 60%;
  }
  &--w70 {
    width: 70%;
  }
  &--w80 {
    width: 80%;
  }
  &--w85 {
    width: 85%;
  }
  &--w90 {
    width: 90%;
  }
}
.qpf-sk-divider {
  height: 1px;
  background: #e8ecf2;
  margin: 18px 0;
}
.qpf-sk-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  > div {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
}
.qpf-sk-table {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}
@keyframes qpfSkShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
@media (max-width: 768px) {
  .qpf-sk-grid {
    grid-template-columns: 1fr 1fr;
  }
  .qpf-sk-row {
    flex-direction: column;
  }
}

.qpf-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
  -webkit-overflow-scrolling: touch;
  /* Permitir selección de texto en todo el contenido del preview */
  user-select: text;
  -webkit-user-select: text;
}

.bg-color-card {
  background: #f7f8f9;
}

// Logo
.qpf-body .logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  img {
    max-width: 180px;
    height: auto;
    display: block;
    border-radius: 8px;
  }
}

// ═══ Jerarquía visual — datos principales oscuros/bold, labels sutiles ═══

// Títulos del negocio
.qpf-body .info {
  h2 {
    color: $primary;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
  }
  h4 {
    color: $primary;
    font-size: 14px;
    font-weight: 600;
  }
  p.font-semi-bold {
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
  }
}

// Labels y valores — idéntico a QuoteView/InvoiceView
.qpf-body .card-quotes-view-logo {
  padding-bottom: 20px;
  border-bottom: 1px solid #e8ecf1;
}
.qpf-body .card-quotes-view-info {
  padding: 16px 0;
  border-bottom: 1px solid #e8ecf1;
  label,
  label.font-semi-bold {
    font-size: 11px !important;
    color: #9ca3af !important;
    display: block;
    font-weight: 400 !important;
    text-transform: none !important;
    letter-spacing: normal !important;
    margin-bottom: 2px;
  }
  p,
  p.m-0 {
    color: $primary;
    font-weight: 600;
    font-size: 14px;
  }
}

// Table headers — hereda estilos de main.scss (.table-items-header)
// No se sobreescribe, el global ya tiene: uppercase, 11px, #6b7280, border-bottom 2px

// Rows de productos — nombre bold oscuro, detalle gris
.qpf-body .card-quotes-view-body-products {
  p {
    color: $primary;
    font-size: 13px;
  }
  strong {
    color: $primary;
    font-weight: 700;
    font-size: 14px;
  }
  .text-muted {
    color: #6b7280 !important;
    font-size: 12px;
  }
}

// Specs box
.qpf-body .card-quotes-view-aditionals {
  background: #f9fafb !important;
  border: 1px solid #e8ecf2;
  border-radius: 10px;

  p.font-semi-bold {
    color: $primary;
    font-size: 13px;
    font-weight: 600;
  }
}

// Payment method box
.qpf-body .card-quotes-view-method-pay {
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 10px;

  p.font-semi-bold {
    color: $primary;
    font-weight: 600;
    font-size: 13px;
  }
  p.m-0:not(.font-semi-bold) {
    color: $primary;
    font-weight: 600;
    font-size: 14px;
  }
}

// Installment cards
.installments-detail {
  border-top: 1px solid #eef1f7;
  padding-top: 12px;
}

.installment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8f9fc;
  border-radius: 8px;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
}

.installment-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.installment-label {
  font-size: 13px;
  font-weight: 600;
  color: #2d2b55;
}

.installment-pct {
  font-size: 12px;
  color: #989aae;
  font-weight: 500;
}

.installment-amount {
  font-size: 13px;
  font-weight: 700;
  color: #1d9e75;
}

// Transition
.preview-fade-enter-active,
.preview-fade-leave-active {
  transition: opacity 0.2s ease;
}
.preview-fade-enter,
.preview-fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
.qpf-dropdown-menu {
  z-index: 2200 !important;

  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      width: 16px;
      text-align: center;
      font-size: 15px;
    }
  }
}
</style>
